import { ReactNode } from "react";
import { useAuth } from "../providers/authProvider";
import { KNOWN_ENVS, useEnv } from "../providers/envProvider";
import { href } from "../route";

type Tab = "groups" | "captures" | "internal" | "settings";
export const Shell = ({ active, children }: { active: Tab; children: ReactNode }) => {
  const { logout } = useAuth();
  const { env, setEnv } = useEnv();
  const envOptions = KNOWN_ENVS.includes(env) ? KNOWN_ENVS : [...KNOWN_ENVS, env];
  const tab = (name: Tab, label: string, to: string) => (
    <a
      href={to}
      className={`px-3 py-1.5 rounded text-sm ${active === name ? "bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900" : "hover:bg-neutral-200 dark:hover:bg-neutral-800"}`}
    >
      {label}
    </a>
  );
  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900 dark:bg-neutral-950 dark:text-neutral-100">
      <header className="border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
        <div className="max-w-6xl mx-auto px-4 h-12 flex items-center gap-3">
          <a href={href.groups()} className="font-semibold mr-2">log-pile</a>
          {tab("groups", "Groups", href.groups())}
          {tab("captures", "All captures", href.captures())}
          <span className="flex-1" />
          <label className="text-xs text-neutral-500 flex items-center gap-1">
            env
            <select
              className="text-xs border rounded px-1.5 py-0.5 bg-white dark:bg-neutral-900 border-neutral-300 dark:border-neutral-700"
              value={env}
              onChange={(e) => {
                if (e.target.value === "__other") {
                  const typed = prompt("Environment name (letters, digits, dashes):", env);
                  if (typed) setEnv(typed);
                } else setEnv(e.target.value);
              }}
            >
              {envOptions.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
              <option value="__other">other…</option>
            </select>
          </label>
          {tab("internal", "log-pile errors", href.internal())}
          {tab("settings", "Settings", href.settings())}
          <span className="text-xs text-neutral-400">v{__APP_VERSION__}</span>
          <button onClick={logout} className="text-sm text-neutral-500 hover:underline">Log out</button>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-4">{children}</main>
    </div>
  );
};

export const Panel = ({ children, className = "" }: { children: ReactNode; className?: string }) => (
  <div className={`bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg ${className}`}>{children}</div>
);

export const Button = ({
  children,
  onClick,
  kind = "default",
  disabled,
  type = "button",
}: {
  children: ReactNode;
  onClick?: () => void;
  kind?: "default" | "primary" | "danger";
  disabled?: boolean;
  type?: "button" | "submit";
}) => {
  const styles = {
    default: "border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800",
    primary: "bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900 hover:opacity-90",
    danger: "border border-red-300 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950",
  }[kind];
  return (
    <button type={type} onClick={onClick} disabled={disabled} className={`text-sm rounded px-2.5 py-1 disabled:opacity-50 ${styles}`}>
      {children}
    </button>
  );
};

export const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <label className="flex flex-col gap-0.5 text-xs text-neutral-500">
    {label}
    {children}
  </label>
);

export const inputClass =
  "text-sm border rounded px-2 py-1 bg-white dark:bg-neutral-900 border-neutral-300 dark:border-neutral-700 text-neutral-900 dark:text-neutral-100";
